import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Switch, Typography, Tag, Space, Tooltip, Divider, Alert, Row, Col } from 'antd';
import { t } from "i18next";
import ApiService from "../../../core/service/ApiService";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import Helper from "../../../core/Helper";
import _ from "lodash";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import { useParams } from "react-router-dom";
import { PatientSummaryRow } from "./PatientSummaryRow";
import AppointmentStateColumn from "../appointment-datagrid/AppointmentStateColumn";
import AppointmentIdColumn from "../appointment-datagrid/AppointmentIdColumn";
import { BankOutlined, CalendarOutlined, PlusOutlined, WalletOutlined, FilterOutlined, CheckCircleOutlined, MedicineBoxOutlined } from '@ant-design/icons';

const { Text } = Typography;

export const AppointmentPatientDataGrid = ({ filter, tableUpdate, onRows, rows, openCalendar, onPayment }) => {
    const params = useParams();
    const prevRows = useRef([])
    const isPageLoad = useRef(false)
    const [selectedTotal, setSelectedTotal] = useState(0)
    const { hasPermission, formatPrice } = useUser();
    const { setData, setPaginationResult, queryModel, setDataGridProps, selectedRows, onFilterPageSort, setSelectedRows, resultModel, refreshDataGrid } = useDataGrid();

    // --- DATA LOADING ---
    const load = async () => {
        const input = {
            ...queryModel,
            filter: {
                ...queryModel.filter,
                startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
                endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
            }
        }

        input.paginationQuery = {
            pageNumber: 1,
            pageSize: 10000, // Client-side pagination gibi davranıyor
        }

        if (!input.filter.patientId) return

        const res = await ApiService.Appointment.GetAppointmentDataGrid(input);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination)
        }
    }

    // --- SEÇİLENLERİN TOPLAMINI HESAPLA ---
    const getTotalBalance = async (selectedIds) => {
        if (selectedIds.length === 0) {
            setSelectedTotal(0);
            return;
        }
        const res = await ApiService.Appointment.GetTotalPatientBalance(params.patientId, selectedIds);
        if (res.success) {
            setSelectedTotal(res.data.selectedTotal)
        }
    }

    // --- KOLON TANIMLARI ---
    const getColumns = () => {
        return [
            {
                width: 60,
                key: 'appointmentId',
                dataIndex: 'appointmentId',
                title: t('Id'),
                render: (text, row) => <AppointmentIdColumn text={text} row={row} onSelect={(row) => setSelectedRows([row])} />
            },
            {
                key: 'startDate',
                dataIndex: 'startDate',
                sorter: true,
                title: t('Date'),
                width: 130,
                render: (text, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 500 }}>{Helper.Date.dateFormat(text).split(' ')[0]}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#888', fontSize: 11 }}>
                            <CalendarOutlined />
                            {Helper.Date.dateFormat(text).split(' ')[1]}
                        </div>
                    </div>
                )
            },
            {
                key: 'details',
                title: t('Details'),
                width: 220,
                render: (_, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Space>
                                <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                                <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: row.unitName }}>{row.unitName}</Text>
                            </Space>
                            {row.institutionName && (
                                <Space size={2} style={{ fontSize: 11, color: '#888' }}>
                                    <BankOutlined />
                                    <Tooltip title={row.institutionName}>
                                        <span style={{ maxWidth: 100, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                                            {row.institutionName}
                                        </span>
                                    </Tooltip>
                                </Space>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Tooltip title={row.appointmentTypeName}>
                                <Text style={{ maxWidth: 130 }} ellipsis>{row.appointmentTypeName}</Text>
                            </Tooltip>
                        </div>
                    </div>
                )
            },
            {
                key: 'stateName',
                dataIndex: 'stateName',
                columnName: 'state.name',
                sorter: true,
                title: t('State'),
                width: 100,
                render: (text, row) => <AppointmentStateColumn text={text} row={row} />
            },
            // FİNANSAL KOLONLAR (Ayrıştırılmış)
            {
                key: 'totalPrice',
                dataIndex: 'totalPrice',
                sorter: true,
                title: t('Price'),
                align: 'right',
                width: 100,
                render: (text, row) => <Text type="secondary">{text === -1 ? '-' : formatPrice(text, { currency: row?.currency || 'TRY' })}</Text>
            },
            {
                key: 'totalPaid',
                dataIndex: 'totalPaid',
                sorter: true,
                title: t('Paid'),
                align: 'right',
                width: 100,
                render: (text, row) => <Text style={{ color: text > 0 ? '#389e0d' : 'inherit' }}>{text === -1 ? '-' : formatPrice(text, { currency: row?.currency || 'TRY' })}</Text>
            },
            {
                key: 'balance',
                dataIndex: 'balance',
                title: t('Balance'),
                sorter: true,
                align: 'right',
                width: 110,
                render: (text, row) => {
                    const hasDebt = row.balance > 0;
                    if (row.totalPrice === -1) return '-';
                    return <Text strong style={{ color: hasDebt ? '#cf1322' : '#389e0d' }}>{formatPrice(row.balance, { currency: row?.currency || 'TRY' })}</Text>
                }
            },
        ]
    }

    // --- EFFECTS ---
    useEffect(() => { load() }, [queryModel.updateKey])
    useEffect(() => { if (tableUpdate) refreshDataGrid() }, [tableUpdate]);
    useEffect(() => { onFilterPageSort({ filter: { ...filter } }) }, [filter]);

    useEffect(() => {
        let columns = getColumns()
        let props = {
            rowKey: 'appointmentId',
            enableRowSelection: onRows, // Seçim aktif
            expandedRowRender: false,
            columns: columns,
            rowClassName: (row) => row.isDeleted ? 'deleted-row' : '',
            antdTableProps: {
                summary: () => <PatientSummaryRow />,
                scroll: { x: 800 },
                size: 'small' // Daha kompakt liste
            }
        }
        props.antdTableProps.pagination = false
        setDataGridProps(props)
    }, [])

    useEffect(() => {
        onRows && onRows(selectedRows)
        if (selectedRows.length > 0) {
            const selectedIds = selectedRows.map(row => row.appointmentId);
            getTotalBalance(selectedIds);
        } else {
            setSelectedTotal(0);
        }
    }, [selectedRows]);

    useEffect(() => {
        if (!_.isEqual(prevRows.current, rows)) {
            prevRows.current = rows
            setSelectedRows(rows)
        }
    }, [rows]);

    useEffect(() => {
        if (!isPageLoad.current && params.appointmentId) {
            isPageLoad.current = true
            setSelectedRows([{ appointmentId: parseInt(params.appointmentId) }])
        }
    }, [resultModel.data])

    // --- RENDER TOOLBAR ---
    const renderToolbar = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, flex: 1 }}>

                {/* SOL: Filtreler */}
                <Space size="small">
                    <Space>
                        <Switch
                            size="small"
                            checked={!(queryModel.filter.stateGroupIdList?.length === 1)}
                            onChange={(checked) => {
                                onFilterPageSort({
                                    filter: { ...queryModel.filter, stateGroupIdList: checked ? [] : [1] }
                                })
                            }}
                        />
                        <Text style={{ fontSize: 13 }}>{t('Show All')}</Text>
                    </Space>

                    <Space>
                        <Switch
                            size="small"
                            checked={queryModel.filter.hideZeroBalance}
                            onChange={(checked) => {
                                onFilterPageSort({
                                    filter: { ...queryModel.filter, hideZeroBalance: checked }
                                })
                            }}
                        />
                        <Text style={{ fontSize: 13 }}>{t('Debt Only')}</Text>
                    </Space>
                </Space>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {selectedRows.length > 0 && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                                <Text>{t('Total Selected')}:</Text>
                                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                                    {formatPrice(selectedTotal)}
                                </Text>
                            </div>

                            {hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                                <Button
                                    type="primary"
                                    danger // Ödeme butonu dikkat çeksin
                                    icon={<WalletOutlined />}
                                    size="small"
                                    onClick={onPayment}
                                >
                                    {t('Receive Payment')}
                                </Button>
                            )}
                        </>
                    )}
                    {hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT) && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCalendar} size="small">
                            {t('New Appointment')}
                        </Button>
                    )}
                </div>
            </div>
        </>
    );

    return (
        <div>
            <Card
                bordered={false}
                style={{ backgroundColor: 'transparent', boxShadow: 'none' }} // İç içe kart görünümünü kaldırdım
                bodyStyle={{ padding: 0 }}
            >
                <DataGrid
                    name={'appointments'}
                    title={renderToolbar}
                />
            </Card>
        </div>
    )
}